import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, Info, Calculator } from 'lucide-react';
import { 
  materialLibrary, 
  materialCategories, 
  stockShapes,
  getCuttingParams,
  calculateRPM 
} from '@/utils/materials';

const MaterialSetup = ({ 
  onMaterialChange, 
  onStockChange,
  onCuttingParamsChange,
  machineType = 'turning',
  initialMaterial = 'alu-6061-t6',
  initialStock = { shape: 'round-bar', diameter: 2.0, length: 4.0 }
}) => {
  const [expanded, setExpanded] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('aluminum');
  const [selectedMaterial, setSelectedMaterial] = useState(initialMaterial);
  const [stockShape, setStockShape] = useState(initialStock.shape);
  const [stockDimensions, setStockDimensions] = useState(initialStock);
  const [showMaterialInfo, setShowMaterialInfo] = useState(false);

  const material = materialLibrary[selectedMaterial];

  // Get materials by category
  const getMaterialsByCategory = (category) => {
    return Object.entries(materialLibrary)
      .filter(([key, mat]) => mat.category === category)
      .map(([key, mat]) => ({ key, ...mat }));
  };

  // Handle material change
  useEffect(() => {
    if (onMaterialChange && material) {
      onMaterialChange(selectedMaterial, material);
    }
    // Calculate recommended cutting params
    if (material && onCuttingParamsChange) {
      const roughing = getCuttingParams(selectedMaterial, 'roughing', machineType);
      const finishing = getCuttingParams(selectedMaterial, 'finishing', machineType);
      if (roughing && finishing) {
        onCuttingParamsChange({
          roughing,
          finishing,
          material: selectedMaterial,
        });
      }
    }
  }, [selectedMaterial, machineType]);

  // Handle stock change
  useEffect(() => {
    if (onStockChange) {
      onStockChange(stockDimensions);
    }
  }, [stockDimensions]);

  const handleStockDimensionChange = (key, value) => {
    setStockDimensions(prev => ({
      ...prev,
      [key]: parseFloat(value) || 0
    }));
  };

  const handleShapeChange = (shape) => {
    setStockShape(shape);
    setStockDimensions(prev => ({ ...prev, shape }));
  };

  return (
    <div className="bg-industrial-surface border border-industrial-border rounded-lg overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-industrial-surface-highlight transition-colors"
        data-testid="material-setup-toggle"
      >
        <div className="flex items-center gap-2">
          <div 
            className="w-4 h-4 rounded-full border-2" 
            style={{ 
              backgroundColor: material ? materialCategories[material.category]?.color : '#666',
              borderColor: 'var(--border)'
            }}
          />
          <span className="font-heading font-semibold text-industrial-readout">
            Material & Stock Setup
          </span>
          {material && (
            <span className="text-xs px-2 py-0.5 bg-industrial-surface-highlight rounded text-industrial-text-secondary">
              {material.name}
            </span>
          )}
        </div>
        {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
      </button>

      {expanded && (
        <div className="px-4 pb-4 border-t border-industrial-border space-y-4 animate-in">
          {/* Material Category Selection */}
          <div className="pt-4">
            <label className="text-xs text-industrial-text-secondary mb-2 block">Material Category</label>
            <div className="flex flex-wrap gap-2">
              {Object.entries(materialCategories).map(([key, cat]) => (
                <button
                  key={key}
                  onClick={() => setSelectedCategory(key)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    selectedCategory === key
                      ? 'bg-industrial-safety text-black'
                      : 'bg-industrial-surface-highlight border border-industrial-border text-industrial-text-secondary hover:border-industrial-safety'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>

          {/* Material Selection */}
          <div>
            <label className="text-xs text-industrial-text-secondary mb-2 block">Select Material</label>
            <select
              value={selectedMaterial}
              onChange={(e) => setSelectedMaterial(e.target.value)}
              className="w-full bg-industrial-bg border border-industrial-border rounded px-3 py-2.5 text-industrial-text"
              data-testid="material-select"
            >
              {getMaterialsByCategory(selectedCategory).map((mat) => (
                <option key={mat.key} value={mat.key}>
                  {mat.name} ({mat.hardness})
                </option>
              ))}
            </select>
          </div>

          {/* Material Properties */}
          {material && (
            <div className="bg-industrial-bg rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-industrial-text">{material.name}</span>
                <button
                  onClick={() => setShowMaterialInfo(!showMaterialInfo)}
                  className="p-1 rounded hover:bg-industrial-surface-highlight"
                >
                  <Info className="w-4 h-4 text-industrial-readout" />
                </button>
              </div>
              
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <span className="text-industrial-text-secondary">Hardness: </span>
                  <span className="text-industrial-text">{material.hardness}</span>
                </div>
                <div>
                  <span className="text-industrial-text-secondary">Machinability: </span>
                  <span className={`${
                    material.machinability === 'Excellent' ? 'text-industrial-success' :
                    material.machinability === 'Good' || material.machinability === 'Very Good' ? 'text-industrial-readout' :
                    material.machinability === 'Fair' ? 'text-industrial-warning' :
                    'text-industrial-danger'
                  }`}>{material.machinability}</span>
                </div>
              </div>

              {showMaterialInfo && (
                <div className="mt-3 pt-3 border-t border-industrial-border text-xs space-y-2">
                  <p className="text-industrial-text-secondary">{material.applications}</p>
                  <p className="text-industrial-text-secondary italic">{material.notes}</p>
                </div>
              )}
            </div>
          )}

          {/* Cutting Data Preview */}
          {material && (
            <div className="bg-industrial-success/10 border border-industrial-success/30 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <Calculator className="w-4 h-4 text-industrial-success" />
                <span className="text-sm font-medium text-industrial-success">Recommended Cutting Data</span>
              </div>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <span className="text-industrial-text-secondary block">Roughing SFM:</span>
                  <span className="text-industrial-text font-mono">
                    {machineType === 'turning' 
                      ? `${material.turning.roughing.sfm[0]}-${material.turning.roughing.sfm[1]}`
                      : `${material.milling.roughing.sfm[0]}-${material.milling.roughing.sfm[1]}`}
                  </span>
                </div>
                <div>
                  <span className="text-industrial-text-secondary block">Finishing SFM:</span>
                  <span className="text-industrial-text font-mono">
                    {machineType === 'turning'
                      ? `${material.turning.finishing.sfm[0]}-${material.turning.finishing.sfm[1]}`
                      : `${material.milling.finishing.sfm[0]}-${material.milling.finishing.sfm[1]}`}
                  </span>
                </div>
                <div>
                  <span className="text-industrial-text-secondary block">Rough Feed ({machineType === 'turning' ? 'IPR' : 'IPT'}):</span>
                  <span className="text-industrial-text font-mono">
                    {machineType === 'turning'
                      ? `${material.turning.roughing.ipr[0]}-${material.turning.roughing.ipr[1]}`
                      : `${material.milling.roughing.ipt[0]}-${material.milling.roughing.ipt[1]}`}
                  </span>
                </div>
                <div>
                  <span className="text-industrial-text-secondary block">Finish Feed ({machineType === 'turning' ? 'IPR' : 'IPT'}):</span>
                  <span className="text-industrial-text font-mono">
                    {machineType === 'turning'
                      ? `${material.turning.finishing.ipr[0]}-${material.turning.finishing.ipr[1]}`
                      : `${material.milling.finishing.ipt[0]}-${material.milling.finishing.ipt[1]}`}
                  </span>
                </div>
              </div>
              
              {/* RPM Calculator */}
              {stockDimensions.diameter && (
                <div className="mt-3 pt-3 border-t border-industrial-success/30 text-xs">
                  <span className="text-industrial-text-secondary">Est. RPM @ ø{stockDimensions.diameter}": </span>
                  <span className="text-industrial-success font-mono">
                    {calculateRPM(
                      machineType === 'turning' 
                        ? (material.turning.roughing.sfm[0] + material.turning.roughing.sfm[1]) / 2
                        : (material.milling.roughing.sfm[0] + material.milling.roughing.sfm[1]) / 2,
                      stockDimensions.diameter
                    )} RPM
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Stock Shape Selection */}
          <div>
            <label className="text-xs text-industrial-text-secondary mb-2 block">Stock Shape</label>
            <div className="grid grid-cols-4 gap-2">
              {Object.entries(stockShapes).slice(0, 4).map(([key, shape]) => (
                <button
                  key={key}
                  onClick={() => handleShapeChange(key)}
                  data-testid={`stock-${key}`}
                  className={`p-2 rounded-lg border text-center transition-all text-xs ${
                    stockShape === key
                      ? 'bg-industrial-readout/20 border-industrial-readout text-industrial-readout'
                      : 'bg-industrial-surface-highlight border-industrial-border text-industrial-text-secondary hover:border-industrial-readout'
                  }`}
                >
                  {shape.name}
                </button>
              ))}
            </div>
          </div>

          {/* Stock Dimensions */}
          <div>
            <label className="text-xs text-industrial-text-secondary mb-2 block">Stock Dimensions (inches)</label>
            <div className="grid grid-cols-2 gap-3">
              {stockShape === 'round-bar' && (
                <>
                  <div>
                    <label className="text-xs text-industrial-text-secondary mb-1 block">Diameter</label>
                    <input
                      type="number"
                      step="0.001"
                      value={stockDimensions.diameter || 2.0}
                      onChange={(e) => handleStockDimensionChange('diameter', e.target.value)}
                      className="w-full bg-industrial-bg border border-industrial-border rounded px-3 py-2 text-industrial-text font-mono"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-industrial-text-secondary mb-1 block">Length</label>
                    <input
                      type="number"
                      step="0.001"
                      value={stockDimensions.length || 4.0}
                      onChange={(e) => handleStockDimensionChange('length', e.target.value)}
                      className="w-full bg-industrial-bg border border-industrial-border rounded px-3 py-2 text-industrial-text font-mono"
                    />
                  </div>
                </>
              )}
              {stockShape === 'hex-bar' && (
                <>
                  <div>
                    <label className="text-xs text-industrial-text-secondary mb-1 block">Across Flats</label>
                    <input
                      type="number"
                      step="0.001"
                      value={stockDimensions.acrossFlats || 1.5}
                      onChange={(e) => handleStockDimensionChange('acrossFlats', e.target.value)}
                      className="w-full bg-industrial-bg border border-industrial-border rounded px-3 py-2 text-industrial-text font-mono"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-industrial-text-secondary mb-1 block">Length</label>
                    <input
                      type="number"
                      step="0.001"
                      value={stockDimensions.length || 4.0}
                      onChange={(e) => handleStockDimensionChange('length', e.target.value)}
                      className="w-full bg-industrial-bg border border-industrial-border rounded px-3 py-2 text-industrial-text font-mono"
                    />
                  </div>
                </>
              )}
              {stockShape === 'tube' && (
                <>
                  <div>
                    <label className="text-xs text-industrial-text-secondary mb-1 block">Outer Dia</label>
                    <input
                      type="number"
                      step="0.001"
                      value={stockDimensions.outerDia || 2.0}
                      onChange={(e) => handleStockDimensionChange('outerDia', e.target.value)}
                      className="w-full bg-industrial-bg border border-industrial-border rounded px-3 py-2 text-industrial-text font-mono"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-industrial-text-secondary mb-1 block">Inner Dia</label>
                    <input
                      type="number"
                      step="0.001"
                      value={stockDimensions.innerDia || 1.5}
                      onChange={(e) => handleStockDimensionChange('innerDia', e.target.value)}
                      className="w-full bg-industrial-bg border border-industrial-border rounded px-3 py-2 text-industrial-text font-mono"
                    />
                  </div>
                </>
              )}
              {(stockShape === 'square-bar' || stockShape === 'rect-bar' || stockShape === 'plate') && (
                <>
                  <div>
                    <label className="text-xs text-industrial-text-secondary mb-1 block">Width X</label>
                    <input
                      type="number"
                      step="0.001"
                      value={stockDimensions.width || 4.0}
                      onChange={(e) => handleStockDimensionChange('width', e.target.value)}
                      className="w-full bg-industrial-bg border border-industrial-border rounded px-3 py-2 text-industrial-text font-mono"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-industrial-text-secondary mb-1 block">Length Y</label>
                    <input
                      type="number"
                      step="0.001"
                      value={stockDimensions.length || 4.0}
                      onChange={(e) => handleStockDimensionChange('length', e.target.value)}
                      className="w-full bg-industrial-bg border border-industrial-border rounded px-3 py-2 text-industrial-text font-mono"
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaterialSetup;
